import { Component, OnDestroy, OnInit } from "@angular/core";
import { LIST_SELECTOR } from "../../../../../crossconcern/utilities/properties/selector.property";
import { BaseComponent } from "../../../../base/base.component";
import { AppComponent } from "../../../../app.component";
import { Router } from "@angular/router";
import { Location } from "@angular/common";
import { AlertUtils } from "../../../../../crossconcern/utilities/utils/alert.utils";
import { ListInfo } from "../../../../../datastore/entities/listinfo.model";
import { TableInfo } from "../../../../../crossconcern/models/tablelist/tableinfo.model";
import {
    PRIVATE_PROMOTION_ADD_ROUTE,
    PRIVATE_PROMOTION_EDIT_ROUTE
} from "../../../../../crossconcern/utilities/properties/routename.property";
import { finalize } from "rxjs/operators";
import { TableInfoHeader } from "../../../../../crossconcern/models/tablelist/tableinfoheader.model";
import { ButtonsModel } from "../../../../../crossconcern/models/tablelist/button.model";
import {
    TABLELIST_BUTTON_COLOR_4, TABLELIST_BUTTON_COLOR_8, TABLELIST_BUTTON_ICON_CLASS_DELETE, TABLELIST_BUTTON_ICON_CLASS_EDIT
} from "../../../../../crossconcern/utilities/properties/tablelist.property";
import { TABLELIST_ITEMS_PER_PAGE } from "../../../../../crossconcern/utilities/properties/base.property";
import { PromotionModel } from "../../../../../datastore/entities/promotion.model";
import { PhotosphereManager } from "../../../../../business/managers/photosphere.manager";
import { GenericService } from "../../../../../crossconcern/services/generic.services";
import { Subscription } from "rxjs";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: LIST_SELECTOR,
    templateUrl: "./promotionlist.component.html"
})
export class PromotionPrivateListComponent extends BaseComponent implements OnInit, OnDestroy {
    public promotions: PromotionModel[];
    public listInfo: ListInfo;
    public tableInfo: TableInfo;
    public searchText: string;

    private currentPage = 0;

    private onNewCalledSubscription: Subscription;

    constructor(
        protected appComponent: AppComponent,
        protected router: Router,
        protected location: Location,
        protected alertUtils: AlertUtils,
        protected genericService: GenericService,
        protected translate: TranslateService,
        private photosphereManager: PhotosphereManager) {
        super(appComponent, router, alertUtils, location);
        this.subscribeGenericServices();
    }

    public ngOnInit() {
        this.getPromotions();
    }

    public ngOnDestroy() {
        this.unsubscribeServices();
    }

    public onEditClick(index: number) {
        const promotion = this.promotions[index];
        this.goToRouteByName(PRIVATE_PROMOTION_EDIT_ROUTE, { id: promotion.uuid });
    }

    public onDeleteClick(index: number) {
        const promotion = this.promotions[index];
        this.alertUtils.showConfirmAlert(
            "PROMOTION_LIST_COMPONENT_DELETE_CONFIRM_TITLE_ALERT",
            "PROMOTION_LIST_COMPONENT_DELETE_CONFIRM_TEXT_ALERT",
            () => {
                this.appComponent.startLoading();
                this.photosphereManager.deletePromotion(promotion.uuid)
                    .pipe(
                        finalize(() => this.appComponent.stopLoading())
                    ).subscribe(() => {
                        this.getPromotions();
                    });
            },
            null
        );
    }

    public getPromotions() {
        this.appComponent.startLoading();
        this.photosphereManager.getPromotions(15, this.currentPage, null, null).pipe(
            finalize(() => this.appComponent.stopLoading())
        ).subscribe(
            (promotions) => {
                this.listInfo = promotions.listInfo;
                this.promotions = promotions.promotions;
                this.setTable();
            });
    }

    public onSearchConfirmed(searchText: string) {
        this.searchText = searchText;
        this.currentPage = 0;
        this.getPromotions();
    }

    public onPageChanged(num: number) {
        this.currentPage = num;
        this.getPromotions();
    }

    public onAdd() {
        // needed for aot compilation
    }

    private setTable() {
        const headers = {
            "title": new TableInfoHeader(
                "PROMOTION_LIST_COMPONENT_HEADER_NAME",
                false,
                false,
                false
            ),
            "nurseries": new TableInfoHeader(
                "PROMOTION_LIST_COMPONENT_HEADER_NURSERIES",
                false,
                false,
                false
            ),
            "products": new TableInfoHeader(
                "PROMOTION_LIST_COMPONENT_HEADER_PRODUCTS",
                false,
                false,
                false
            ),
            "featured": new TableInfoHeader(
                "PROMOTION_LIST_COMPONENT_HEADER_FEATURED",
                false,
                false,
                false
            ),
            "actions": new TableInfoHeader(
                "PROMOTION_LIST_COMPONENT_HEADER_ACTIONS",
                false,
                false,
                false
            )
        };

        const items: unknown[] = [];
        for (const promotion of this.promotions) {
            items.push({
                "title": (promotion.title) ? promotion.title : "",
                "nurseries": this.getNurseriesString(promotion),
                "products": this.getProductsString(promotion),
                "featured": promotion.featured != null && promotion.featured
                    ? this.translate.instant("YES")
                    : this.translate.instant("NO")
            });
        }

        const buttons: ButtonsModel[] = [];
        buttons.push(new ButtonsModel(
            "NURSERY_COMPONENT_TABLE_BUTTON_VIEW",
            TABLELIST_BUTTON_COLOR_4,
            TABLELIST_BUTTON_ICON_CLASS_EDIT,
            (item) => {
                this.onEditClick(item as number);
            })
        );
        buttons.push(new ButtonsModel(
            "PROMOTION_LIST_COMPONENT_TABLE_BUTTON_DELETE",
            TABLELIST_BUTTON_COLOR_8,
            TABLELIST_BUTTON_ICON_CLASS_DELETE,
            (item) => {
                this.onDeleteClick(item as number);
            })
        );

        this.tableInfo = new TableInfo(headers, items, TABLELIST_ITEMS_PER_PAGE, buttons);
    }

    private getNurseriesString(promotion: PromotionModel) {
        if(promotion.nurseries && promotion.nurseries.length > 0) {
            return promotion.nurseries.map((nurseries) => nurseries.nursery.name).join(", ");
        } else {
            return "-";
        }
    }

    private getProductsString(promotion: PromotionModel) {
        if(promotion.products && promotion.products.length > 0) {
            return promotion.products.map((product) => product.product.varDex).join(", ");
        } else {
            return "-";
        }
    }

    private subscribeGenericServices() {
        this.onNewCalledSubscription = this.genericService.onNewCalled.subscribe(() => {
            this.goToRouteByName(PRIVATE_PROMOTION_ADD_ROUTE);
        });
    }

    private unsubscribeServices() {
        if(this.onNewCalledSubscription) {
            this.onNewCalledSubscription.unsubscribe();
        }
    }
}